import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Fingerprint, Trash2, Plus, Pencil, Check, X as XIcon, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  isPlatformPasskeyAvailable,
  registerPasskey,
  listPasskeys,
  deletePasskey,
  renamePasskey,
  rememberPasskeyEmail,
  guessDeviceName,
} from '../lib/webauthn';

function timeAgo(iso) {
  if (!iso) return 'Never used';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function PasskeyManager({ userEmail }) {
  const [supported, setSupported] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listPasskeys();
      setItems(rows);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const ok = await isPlatformPasskeyAvailable();
      setSupported(ok);
      if (ok) await refresh();
      else setLoading(false);
    })();
  }, [refresh]);

  const handleAdd = async () => {
    setAdding(true);
    try {
      await registerPasskey({ deviceName: guessDeviceName() });
      if (userEmail) rememberPasskeyEmail(userEmail);
      toast.success('Passkey added. Sign in with a look or tap next time.');
      await refresh();
    } catch (err) {
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        // user cancelled — no toast
      } else {
        toast.error(err?.response?.data?.detail || 'Could not add passkey.');
      }
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (credentialId) => {
    if (!window.confirm('Remove this device from biometric sign-in? You can add it back anytime.')) return;
    try {
      await deletePasskey(credentialId);
      toast.success('Device removed.');
      setItems((prev) => prev.filter((r) => r.credential_id !== credentialId));
    } catch {
      toast.error('Could not remove device.');
    }
  };

  const startEdit = (row) => {
    setEditingId(row.credential_id);
    setEditValue(row.name || '');
  };

  const saveEdit = async () => {
    const name = editValue.trim();
    if (!name) {
      setEditingId(null);
      return;
    }
    try {
      await renamePasskey(editingId, name);
      setItems((prev) => prev.map((r) => (r.credential_id === editingId ? { ...r, name } : r)));
      setEditingId(null);
      toast.success('Name updated.');
    } catch {
      toast.error('Could not rename device.');
    }
  };

  return (
    <Card className="border-[#36454F]/15 bg-white" data-testid="passkey-section">
      <CardHeader>
        <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F] flex items-center">
          <Fingerprint className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
          Biometric Sign-In
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!supported ? (
          <div className="flex items-start p-3 bg-[#F5F5F5] rounded-sm" data-testid="passkey-unsupported">
            <ShieldOff className="w-5 h-5 text-[#36454F]/40 mr-3 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-['Manrope'] text-[#36454F]/80">
                This device or browser doesn&apos;t support Face ID, Touch ID, Windows Hello, or fingerprint sign-in.
              </p>
              <p className="text-xs text-[#36454F]/50 font-['Manrope'] mt-1">
                Try again from your phone or a modern browser (Safari 16+, Chrome 108+, Edge 108+).
              </p>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#36454F]/70 font-['Manrope'] mb-4 leading-relaxed">
              Sign in without typing your password. Register any device you use — iPhone, Android, Mac, or Windows.
              Your biometric data never leaves the device; we only store a public key.
            </p>

            {loading ? (
              <p className="text-sm text-[#36454F]/50 font-['Manrope']" data-testid="passkey-loading">Loading devices…</p>
            ) : items.length === 0 ? (
              <div className="p-4 bg-[#F5F5F5] rounded-sm text-center" data-testid="passkey-empty">
                <p className="text-sm text-[#36454F]/70 font-['Manrope']">
                  No devices registered yet.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-[#36454F]/10 border border-[#36454F]/10 rounded-sm" data-testid="passkey-list">
                {items.map((row) => (
                  <li key={row.credential_id} className="flex items-center justify-between p-3" data-testid={`passkey-row-${row.credential_id.slice(0, 8)}`}>
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-sm bg-[#B76E79]/10 flex items-center justify-center mr-3 flex-shrink-0">
                        <Fingerprint className="w-4 h-4 text-[#B76E79]" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        {editingId === row.credential_id ? (
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
                            className="h-8 text-sm font-['Manrope'] border-[#B76E79]/40"
                            autoFocus
                            maxLength={60}
                            data-testid="passkey-rename-input"
                          />
                        ) : (
                          <p className="font-['Manrope'] font-medium text-[#36454F] text-sm truncate">
                            {row.name || 'Passkey device'}
                          </p>
                        )}
                        <p className="text-xs text-[#36454F]/50 font-['Manrope']">
                          Last used {timeAgo(row.last_used_at)}
                          {' · Added '}{new Date(row.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      {editingId === row.credential_id ? (
                        <>
                          <button onClick={saveEdit} className="p-2 text-[#36454F]/60 hover:text-[#B76E79]" title="Save" data-testid="passkey-rename-save">
                            <Check className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          <button onClick={() => setEditingId(null)} className="p-2 text-[#36454F]/60 hover:text-[#36454F]" title="Cancel">
                            <XIcon className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(row)}
                            className="p-2 text-[#36454F]/50 hover:text-[#B76E79] transition-colors"
                            title="Rename"
                            data-testid={`passkey-rename-${row.credential_id.slice(0, 8)}`}
                          >
                            <Pencil className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => handleDelete(row.credential_id)}
                            className="p-2 text-[#36454F]/50 hover:text-[#D9534F] transition-colors"
                            title="Remove"
                            data-testid={`passkey-remove-${row.credential_id.slice(0, 8)}`}
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <Button
              onClick={handleAdd}
              disabled={adding}
              className="mt-4 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
              data-testid="passkey-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {adding ? 'Adding…' : 'Add this device'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
